<script lang="ts" setup>
/*=============================================================================*/
/*                               Imports                                       */
/*=============================================================================*/
import Plotly from "plotly.js-dist-min"
import { computed, inject, ref, watch } from "vue"
import { useIrveStore } from "@modules/irve/stores/irve.store"
import { formatDate } from "@core/helpers"
import { useI18n } from "vue-i18n"
import type { Services } from "@services"
import { useFetchWrapper } from "@core/composables"
import { useToast } from "primevue/usetoast"

/*=============================================================================*/
/*                                  Props                                      */
/*=============================================================================*/
/**
 *   `title` : Titre du graphique
 *   `graphs` : Liste des graphiques affichées
 *   `value` : Graphique au format string JSON du graphique Plotly
 *   `name` : Nom donnée dans le sélecteur de graphique
 */
type Props = {
    title: string
    graphs: {
        graph_id: string
        name: string
        graph?: string
    }[]
}

const props = defineProps<Props>()

const { $createRapport } = inject<Services>("services")!

const irveStore = useIrveStore()

const modelGraph = ref<string | null>(null)

const url = ref("")

const getGraphSelected = computed(() => {
    return props.graphs.find((graph) => graph.graph_id === modelGraph.value)
})

const getGrapOptions = computed(() => {
    return (
        props.graphs?.map((graph) => ({
            value: graph.graph_id,
            name: graph.name,
        })) ?? []
    )
})

watch(
    () => props.graphs,
    () => {
        modelGraph.value = props.graphs?.[0]?.graph_id
    },
    { deep: true, immediate: true }
)

/**
 * Récupère le graphique sélectionné au format JSON
 */
const getGraph = computed(() => {
    return JSON.parse(getGraphSelected.value?.graph ?? "")
})

/**
 * Quand le graphique est chargé, zoom sur une semaine de données
 */
function onLoaded() {
    // zoom sur une semaine de données après 2 secondes

    if (!getGraph.value?.layout?.xaxis) return

    const startDate = new Date(getGraph.value?.layout?.xaxis?.range[0] ?? "")
    const endDate = new Date(getGraph.value?.layout?.xaxis?.range[1] ?? "")

    // Récupère la moitier de la période
    const middleDate = new Date(
        startDate.getTime() + (endDate.getTime() - startDate.getTime()) / 2
    )
    // En fonction de la date du milieu, on peut déterminer la semaine en cours du lundi au dimanche
    const startWeek = new Date(
        middleDate.setDate(middleDate.getDate() - middleDate.getDay())
    )
    const endWeek = new Date(middleDate.setDate(middleDate.getDate() + 6))

    Plotly.relayout(getGraphSelected.value?.graph_id ?? "", {
        "xaxis.range": [
            formatDate(startWeek, "YYYY-MM-DD"),
            formatDate(endWeek, "YYYY-MM-DD"),
        ], // Mise à jour de la plage
    })
}

function onClickExportData() {
    // transforme le titre en nom de fichier en retirant les espaces et accents + miniuscule
    const filename = `${props.title
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        // remplace les "`"
        .replace(/'/g, "")
        .replace(/\s/g, "_")
        .toLowerCase()}.xlsx`

    const sheet = {
        sheetname: "Donnée",
        columns: [],
    } as {
        sheetname: string
        columns: { colname: string; values: any[] }[]
    }

    // Récupérration  des dates à partir du premier tracé
    const dates = getGraph.value.data?.[0]?.x ?? []

    // Ajout de la colonne des dates
    sheet.columns.push({
        colname: "Date",
        values: dates,
    })

    // pour chaque trace, on récupère les données et on crée une colonne
    getGraph.value.data?.forEach(
        (trace: {
            name: string
            y: any[]
            meta: [string, { unit: string; type?: string }]
        }) => {
            sheet.columns.push({
                colname: `${trace.name} (${trace.meta?.[1]?.unit})`,
                values: trace.y,
            })
        }
    )

    const sheets = [sheet]

    fetchCreateRapport.fetch({ filename, sheets })
}

const toast = useToast()
const fetchCreateRapport = useFetchWrapper(
    async ({
        filename,
        sheets,
    }: {
        filename: string
        sheets: {
            sheetname: string
            columns: {
                colname: string
                values: any[]
            }[]
        }[]
    }) => {
        if (!url.value) {
            url.value = await $createRapport(filename, sheets)
        }

        window.open(url.value)
        toast.add({
            severity: "success",
            summary: "Donnée exportée",
            life: 3000,
        })
    }
)

const { t } = useI18n({
    messages: {
        fr: {
            export_data: "Exporter les données",
        },
    },
})
</script>

<template>
    <div class="irve-view-data-chart">
        <div class="mb-8 flex items-center justify-between gap-2">
            <h1 class="font-bold text-primary-700 text-lg">{{ title }}</h1>
            <BaseButton
                v-if="getGraphSelected?.graph && !irveStore.isFetchingAnalysis"
                @click="onClickExportData"
                :label="t('export_data')"
                severity="secondary"
                text
                :loading="fetchCreateRapport.isFetching.value"
                :disabled="fetchCreateRapport.isFetching.value"
            />
            <BaseSelectButton
                v-if="getGrapOptions.length > 1"
                v-model="modelGraph"
                :options="getGrapOptions"
                option-label="name"
                option-value="value"
            />
        </div>
        <PlotlyChart
            v-if="getGraphSelected?.graph && !irveStore.isFetchingAnalysis"
            :graph="getGraph"
            @loaded="onLoaded"
            :id="getGraphSelected.graph_id"
        ></PlotlyChart>
        <BaseChartSkeleton v-else />
        <BaseToast></BaseToast>
    </div>
</template>
